import type { Dispatch, SetStateAction } from 'react'
import { useCallback } from 'react'

import type { TreeItem } from '@graftcode/design-system'

import { getNavigationUrls } from '@common/core/config/navigation'
import { AppRoute } from '@common/core/enums'

import { useNavigationStore } from '@/core/stores/navigationStore'

interface UseLayoutActionsParams {
  setMobileMenuOpen: Dispatch<SetStateAction<boolean>>
  refetch: () => Promise<unknown>
}

export function useLayoutActions({ setMobileMenuOpen, refetch }: UseLayoutActionsParams) {
  const handleSignUpClick = useCallback(() => {
    const urls = getNavigationUrls()
    window.location.href = urls.portal
  }, [])

  const handleRefresh = useCallback(() => {
    void refetch()
  }, [refetch])

  const handleCreateGateway = useCallback(() => {
    const { portal } = getNavigationUrls()
    window.location.href = `${portal}/${AppRoute.SERVICE_DEPLOYMENT_METHOD}`
  }, [])

  const handleMenuToggle = useCallback(() => {
    setMobileMenuOpen((prev) => !prev)
  }, [setMobileMenuOpen])

  const handleMobileMenuClose = useCallback(() => {
    setMobileMenuOpen(false)
  }, [setMobileMenuOpen])

  const handleProfileClick = useCallback((item: string) => {
    if (item === 'logout') {
      window.location.href = '/logout'
    }
  }, [])

  const handleNavigationClick = useCallback((url: string) => {
    window.location.href = `/${url}`
  }, [])

  const handleTreeItemClick = useCallback(
    (item: TreeItem) => {
      const newPath = item.id === 'root' ? [] : item.id.split('/')
      const isLeaf = !item.children || item.children.length === 0

      if (isLeaf) {
        setMobileMenuOpen(false)
        setTimeout(() => useNavigationStore.getState().navigateTo(newPath), 350)
      } else {
        useNavigationStore.getState().navigateTo(newPath)
      }
    },
    [setMobileMenuOpen]
  )

  const handleTreeItemToggle = useCallback((item: TreeItem, expanded: boolean) => {
    if (item.id === 'root' && !expanded) {
      return
    }

    const store = useNavigationStore.getState()
    if (expanded) {
      const newPath = item.id === 'root' ? [] : item.id.split('/')
      store.navigateTo(newPath)
    } else {
      store.toggleExpanded(item.id)
      const itemPath = item.id.split('/')
      const parentPath = itemPath.slice(0, -1)
      store.navigateTo(parentPath)
    }
  }, [])

  return {
    handleSignUpClick,
    handleRefresh,
    handleCreateGateway,
    handleMenuToggle,
    handleMobileMenuClose,
    handleProfileClick,
    handleNavigationClick,
    handleTreeItemClick,
    handleTreeItemToggle,
  }
}
